import { isNavKeyExemptFromAppScope } from "./navAppExemptKeys";
import { MAX_APPS_SEED } from "../data/maxAppsDefinitions";

// Create a fast lookup map for key -> moduleCode
const KEY_TO_MODULE_MAP = new Map();
MAX_APPS_SEED.forEach(app => {
  app.navKeys?.forEach(key => KEY_TO_MODULE_MAP.set(key, app.code));
  // Also map common module keys directly
  KEY_TO_MODULE_MAP.set(app.code, app.code);
});

/** All module codes from the static catalog (used when tenant has no nav-app scope / legacy). */
export const ALL_SUBSCRIBED_MODULE_CODES = new Set(
  MAX_APPS_SEED.map((a) => String(a.code).toUpperCase())
);

/**
 * Derive which app/module codes are implied by an allowed nav-key set (granular overrides).
 */
export function getModuleCodesForNavKeys(navKeySet) {
  if (!navKeySet || navKeySet.size === 0) return new Set();
  const out = new Set();
  for (const k of navKeySet) {
    const s = String(k);
    const mod =
      KEY_TO_MODULE_MAP.get(s) ||
      KEY_TO_MODULE_MAP.get(s.toUpperCase()) ||
      KEY_TO_MODULE_MAP.get(s.toLowerCase());
    if (mod) out.add(String(mod).toUpperCase());
  }
  return out;
}

/**
 * Pure helpers for nav visibility (RBAC + optional admin "Apps" scope).
 * Used by Command Palette and other nav surfaces — keep rules in one place.
 */

export function isNavNodeVisible(node, ctx) {
  const { hasPermission, navAppScopeActive, navAppAllowedKeys, subscribedModules } = ctx;
  const key = String(node.key || '');
  const upperKey = key.toUpperCase();

  // 1. Check Tenant Module Subscription (Physical gating)
  const moduleCode = KEY_TO_MODULE_MAP.get(key) || KEY_TO_MODULE_MAP.get(upperKey);
  if (moduleCode) {
    // User must have this module in subscribedModules (see NavigationAccessContext).
    const subs = subscribedModules || new Set();
    if (!subs.has(moduleCode.toUpperCase())) {
      return false;
    }
  }

  // 2. Check Admin-defined "App" Scope (Logical filtering)
  if (navAppScopeActive) {
    if (!isNavKeyExemptFromAppScope(key)) {
      if (!key || !navAppAllowedKeys?.has(upperKey)) {
        return false;
      }
    }
  }

  // 3. Check RBAC Permissions
  if (node.permissionCode && !hasPermission(node.permissionCode)) {
    return false;
  }
  return true;
}

export function filterNavItems(items, ctx) {
  if (!items?.length) return [];
  return items.filter((item) => isNavNodeVisible(item, ctx));
}

/**
 * Recursively filter a tree; drops groups whose children are all hidden.
 */
export function filterNavTree(nodes, ctx) {
  if (!nodes?.length) return [];
  return nodes
    .map((node) => {
      if (node.children?.length) {
        const children = filterNavTree(node.children, ctx);
        if (children.length === 0) return null;
        return { ...node, children };
      }
      return isNavNodeVisible(node, ctx) ? node : null;
    })
    .filter(Boolean);
}
